import { useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Center, Loader, Stack, Text } from "@mantine/core";
import { toast } from "sonner";
import { qrApi, type QrResolveResponse } from "@/lib/api/client";

/** The portal page a resolve answer may send the member to: a path of this
 *  portal only. Anything else (`https://…`, `//host`, `/\host`, no leading
 *  slash) falls back to the dashboard — a printed code must never become an
 *  open redirect, whatever the server says. */
export function qrDestination(resp: Pick<QrResolveResponse, "portal_path"> | null | undefined): string {
  const path = resp?.portal_path ?? "";
  // eslint-disable-next-line no-control-regex
  if (!path.startsWith("/") || path.startsWith("//") || path.startsWith("/\\") || /[\\\s\x00-\x1f\x7f]/.test(path)) {
    return "/";
  }
  return path;
}

/** `/q/:uid` — the stable short link printed in QR codes (rooms, desks,
 *  equipment, the screens' member-portal QR). Sign-in happens first (the
 *  route is protected and login returns here); then the backend says which
 *  page this member should see for the object and we go there, replacing
 *  this entry so Back doesn't bounce through the resolver. */
export default function QrResolvePage() {
  const { uid = "" } = useParams<{ uid: string }>();
  const navigate = useNavigate();
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    qrApi
      .resolve(uid)
      .then(({ data }) => {
        if (!data.found) {
          toast.info(data.message || "That code doesn't lead anywhere in the portal any more.");
        }
        navigate(qrDestination(data), { replace: true });
      })
      .catch(() => {
        toast.info("Couldn't open that code — here's the portal home.");
        navigate("/", { replace: true });
      });
  }, [uid, navigate]);

  return (
    <Center mih="60vh">
      <Stack align="center" gap="xs">
        <Loader size="sm" />
        <Text fz="sm" c="dimmed">
          Opening…
        </Text>
      </Stack>
    </Center>
  );
}
